var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState, useEffect, useCallback } from 'react';
import globalStore from '../store';
import '../styles/scrollbar.scss';
var SCROLLBAR_THRESHOLD = 0;
var CustomScrollbar = React.forwardRef(function (props, ref) {
    var children = props.children, className = props.className, style = props.style, _a = props.direction, direction = _a === void 0 ? 'both' : _a, _b = props.hideMouseScroll, hideMouseScroll = _b === void 0 ? false : _b, onChange = props.onChange, _c = props.verticalTrackAriaHidden, verticalTrackAriaHidden = _c === void 0 ? undefined : _c;
    var displayMode = globalStore.useStoreState(function (state) { return state.playerStore; }).displayMode;
    var containerRef = useRef(null);
    var contentRef = useRef(null);
    var verticalThumbRef = useRef(null);
    var horizontalThumbRef = useRef(null);
    // Track container size
    var _d = useState({ width: 0, height: 0 }), containerSize = _d[0], setContainerSize = _d[1];
    // Track content size to determine if scrolling is needed
    var _e = useState({ width: 0, height: 0 }), contentSize = _e[0], setContentSize = _e[1];
    // Thumb sizes and positions
    var _f = useState(0), verticalThumbHeight = _f[0], setVerticalThumbHeight = _f[1];
    var _g = useState(0), verticalThumbTop = _g[0], setVerticalThumbTop = _g[1];
    var _h = useState(0), horizontalThumbWidth = _h[0], setHorizontalThumbWidth = _h[1];
    var _j = useState(0), horizontalThumbLeft = _j[0], setHorizontalThumbLeft = _j[1];
    // Track dragging state
    var dragging = useRef(null);
    var dragStart = useRef({
        mousePos: 0,
        scrollStart: 0,
    });
    var updateThumbs = useCallback(function () {
        var container = containerRef.current;
        var content = contentRef.current;
        if (!container || !content)
            return;
        var containerHeight = containerSize.height;
        var containerWidth = containerSize.width;
        var contentHeight = content.scrollHeight;
        var contentWidth = content.scrollWidth;
        // Update content size state
        setContentSize({ width: contentWidth, height: contentHeight });
        // Vertical thumb height calculation (only if content overflows)
        if (contentHeight > containerHeight && containerHeight > 0) {
            var vThumbHeight = Math.max((containerHeight / contentHeight) * containerHeight, 20);
            setVerticalThumbHeight(vThumbHeight);
            // Vertical thumb top position
            var scrollableHeight = contentHeight - containerHeight;
            var vThumbTop = scrollableHeight > 0
                ? (content.scrollTop / scrollableHeight) * (containerHeight - vThumbHeight)
                : 0;
            setVerticalThumbTop(isNaN(vThumbTop) ? 0 : vThumbTop);
        }
        else {
            setVerticalThumbHeight(0);
            setVerticalThumbTop(0);
        }
        // Horizontal thumb width calculation (only if content overflows)
        if (contentWidth > containerWidth && containerWidth > 0) {
            var hThumbWidth = Math.max((containerWidth / contentWidth) * containerWidth, 20);
            setHorizontalThumbWidth(hThumbWidth);
            // Horizontal thumb left position
            var scrollableWidth = contentWidth - containerWidth;
            var hThumbLeft = scrollableWidth > 0
                ? (content.scrollLeft / scrollableWidth) * (containerWidth - hThumbWidth)
                : 0;
            setHorizontalThumbLeft(isNaN(hThumbLeft) ? 0 : hThumbLeft);
        }
        else {
            setHorizontalThumbWidth(0);
            setHorizontalThumbLeft(0);
        }
    }, [containerSize.height, containerSize.width]);
    useEffect(function () {
        updateThumbs();
    }, [children, updateThumbs]);
    var onScroll = function () {
        var _a, _b;
        updateThumbs();
        onChange === null || onChange === void 0 ? void 0 : onChange(((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.scrollTop) || 0, ((_b = contentRef.current) === null || _b === void 0 ? void 0 : _b.scrollLeft) || 0);
    };
    var onVerticalThumbMouseDown = function (e) {
        var _a;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        e.preventDefault();
        dragging.current = 'vertical';
        dragStart.current = {
            mousePos: clientY,
            scrollStart: ((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.scrollTop) || 0,
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('touchmove', onMouseMove);
        document.addEventListener('touchend', onMouseUp);
    };
    var onHorizontalThumbMouseDown = function (e) {
        var _a;
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        e.preventDefault();
        dragging.current = 'horizontal';
        dragStart.current = {
            mousePos: clientX,
            scrollStart: ((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.scrollLeft) || 0,
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('touchmove', onMouseMove);
        document.addEventListener('touchend', onMouseUp);
    };
    var onMouseMove = function (e) {
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        if (!dragging.current || !contentRef.current || !containerRef.current)
            return;
        var container = containerRef.current;
        var content = contentRef.current;
        if (dragging.current === 'vertical') {
            var containerHeight = containerSize.height;
            var contentHeight = content.scrollHeight;
            var delta = clientY - dragStart.current.mousePos;
            var scrollableHeight = contentHeight - containerHeight;
            var trackHeight = containerHeight - verticalThumbHeight;
            var scrollDelta = (delta / trackHeight) * scrollableHeight;
            content.scrollTop = dragStart.current.scrollStart + scrollDelta;
        }
        if (dragging.current === 'horizontal') {
            var containerWidth = containerSize.width;
            var contentWidth = content.scrollWidth;
            var delta = clientX - dragStart.current.mousePos;
            var scrollableWidth = contentWidth - containerWidth;
            var trackWidth = containerWidth - horizontalThumbWidth;
            var scrollDelta = (delta / trackWidth) * scrollableWidth;
            content.scrollLeft = dragStart.current.scrollStart + scrollDelta;
        }
    };
    var onMouseUp = function () {
        dragging.current = null;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.removeEventListener('touchmove', onMouseMove);
        document.removeEventListener('touchend', onMouseUp);
    };
    useEffect(function () {
        var container = containerRef.current;
        var content = contentRef.current;
        if (!container || !content)
            return;
        // Observe container size — defer setState to avoid updating during React's commit phase
        var resizeRafId = null;
        var resizeObserver = new ResizeObserver(function (entries) {
            if (resizeRafId)
                cancelAnimationFrame(resizeRafId);
            resizeRafId = requestAnimationFrame(function () {
                resizeRafId = null;
                for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
                    var entry = entries_1[_i];
                    if (entry.target === container) {
                        var cr = entry.contentRect;
                        setContainerSize({ width: Math.round(cr.width), height: Math.round(cr.height) });
                    }
                }
                updateThumbs();
            });
        });
        resizeObserver.observe(container);
        // Also listen to content size changes to update thumbs
        resizeObserver.observe(content);
        // Watch for DOM mutations (e.g. collapsible sections toggling)
        // that change scrollHeight without changing the element's rendered size.
        // Use rAF to defer the update so it doesn't fire during React's commit phase
        // (which would cause "Cannot update component while rendering" warnings).
        var mutationRafId = null;
        var mutationObserver = new MutationObserver(function () {
            if (mutationRafId)
                cancelAnimationFrame(mutationRafId);
            mutationRafId = requestAnimationFrame(function () {
                mutationRafId = null;
                updateThumbs();
            });
        });
        mutationObserver.observe(content, { childList: true, subtree: true });
        return function () {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            if (resizeRafId)
                cancelAnimationFrame(resizeRafId);
            if (mutationRafId)
                cancelAnimationFrame(mutationRafId);
        };
    }, [updateThumbs]);
    // === PUBLIC SCROLL API EXPOSED ===
    React.useImperativeHandle(ref, function () { return ({
        get scrollTop() {
            var _a;
            return ((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.scrollTop) || 0;
        },
        set scrollTop(value) {
            if (contentRef.current) {
                contentRef.current.scrollTop = value;
                updateThumbs();
            }
        },
        get scrollLeft() {
            var _a;
            return ((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.scrollLeft) || 0;
        },
        set scrollLeft(value) {
            if (contentRef.current) {
                contentRef.current.scrollLeft = value;
                updateThumbs();
            }
        },
        scrollTo: function (x, y) {
            if (contentRef.current) {
                contentRef.current.scrollLeft = x;
                contentRef.current.scrollTop = y;
                updateThumbs();
            }
        },
        scrollToTop: function () {
            if (contentRef.current) {
                contentRef.current.scrollTop = 0;
                updateThumbs();
            }
        },
        scrollToBottom: function () {
            if (contentRef.current) {
                contentRef.current.scrollTop = contentRef.current.scrollHeight;
                updateThumbs();
            }
        },
    }); });
    return (_jsxs("div", { className: "customScrollbarContainer ".concat(className || ''), style: __assign({ position: 'relative', width: '100%', height: '100%' }, style), ref: containerRef, "aria-hidden": props.ariaHidden ? true : undefined, inert: props.inert ? true : undefined, children: [_jsx("div", __assign({ className: "customScrollbarContent ".concat(hideMouseScroll ? 'hideMouseScroll' : ''), ref: contentRef, onScroll: onScroll }, (props.tabIndex !== undefined ? { tabIndex: props.tabIndex } : {}), { children: children })), (direction === 'vertical' || direction === 'both') &&
                contentSize.height - containerSize.height > SCROLLBAR_THRESHOLD &&
                verticalThumbHeight > 0 && (_jsx("div", { className: "customScrollbarTrack vertical", "aria-hidden": verticalTrackAriaHidden !== null && verticalTrackAriaHidden !== void 0 ? verticalTrackAriaHidden : undefined, children: _jsx("div", { className: "customScrollbarThumb vertical", style: { height: verticalThumbHeight, top: verticalThumbTop }, ref: verticalThumbRef, onMouseDown: onVerticalThumbMouseDown, onTouchStart: onVerticalThumbMouseDown }) })), (direction === 'horizontal' || direction === 'both') &&
                contentSize.width - containerSize.width > SCROLLBAR_THRESHOLD &&
                horizontalThumbWidth > 0 && (_jsx("div", { className: "customScrollbarTrack horizontal ".concat(displayMode === 'smartBoard' && 'updateBottom'), children: _jsx("div", { className: "customScrollbarThumb horizontal", style: { width: horizontalThumbWidth, left: horizontalThumbLeft }, ref: horizontalThumbRef, onMouseDown: onHorizontalThumbMouseDown, onTouchStart: onHorizontalThumbMouseDown }) }))] }));
});
export default CustomScrollbar;
